/**
 * feature-flags.js — Lightweight feature-flag resolution for SnowCubes.
 *
 * Priority: URL param (?key=1) > localStorage > server (window global).
 * URL params auto-persist to localStorage so flags stick across navigations.
 *
 * Usage:
 *   SnowcubesFlags.register('my_flag', { serverKey: '__MY_SERVER_FLAG' });
 *   if (SnowcubesFlags.isEnabled('my_flag')) { ... }
 */
(function (global) {
  'use strict';

  var registry = {};

  // --- Safe helpers (mirror gift-card-core.js, standalone) ----------------

  function safeGetSearchParams() {
    try {
      return new URLSearchParams(global.location?.search || '');
    } catch (_) {
      return new URLSearchParams('');
    }
  }

  function safeGetLocalStorageItem(key) {
    try {
      return global.localStorage?.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function safeSetLocalStorageItem(key, value) {
    try {
      global.localStorage?.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  // --- Core API -----------------------------------------------------------

  /**
   * Register a flag key with optional server global name.
   * @param {string} flagKey  - URL param / localStorage key (e.g. 'sc_gift_ui')
   * @param {object} [opts]
   * @param {string} [opts.serverKey] - window global to check (e.g. '__SC_GIFT_UI_SERVER_ENABLED')
   */
  function register(flagKey, opts) {
    registry[flagKey] = { serverKey: (opts && opts.serverKey) || null, serverValue: undefined };
  }

  /**
   * Derive flag key from a __SC_FLAGS property name.
   * - strip _enabled suffix
   * - strip enable_ after sc_ prefix
   */
  function deriveFlagKey(rawKey) {
    var key = rawKey;
    if (key.slice(-8) === '_enabled') {
      key = key.slice(0, -8);
    }
    if (key.indexOf('sc_enable_') === 0) {
      key = 'sc_' + key.slice(10);
    }
    return key;
  }

  /**
   * Auto-discover flags from window.__SC_FLAGS (or global.__SC_FLAGS).
   * Each property is resolved and stored in the registry.
   */
  function _autoDiscover() {
    var flags = global.__SC_FLAGS;
    if (!flags || typeof flags !== 'object') return;
    for (var rawKey in flags) {
      if (Object.prototype.hasOwnProperty.call(flags, rawKey)) {
        var flagKey = deriveFlagKey(rawKey);
        registry[flagKey] = { serverKey: null, serverValue: !!flags[rawKey], autoDiscovered: true };
      }
    }
  }

  /**
   * Re-run auto-discovery (useful for tests after setting up __SC_FLAGS).
   * Clears previously auto-discovered entries before re-scanning.
   */
  function _init() {
    for (var key in registry) {
      if (Object.prototype.hasOwnProperty.call(registry, key) && registry[key].autoDiscovered) {
        delete registry[key];
      }
    }
    _autoDiscover();
  }

  /**
   * Resolve a flag using the 3-source priority chain.
   * @param {string} flagKey
   * @returns {boolean}
   */
  function isEnabled(flagKey) {
    // 1. URL param — highest priority, persists to localStorage
    var params = safeGetSearchParams();
    var paramVal = params.has(flagKey) ? params.get(flagKey) : null;
    if (paramVal === '1') {
      safeSetLocalStorageItem(flagKey, '1');
      return true;
    }
    if (paramVal === '0') {
      safeSetLocalStorageItem(flagKey, '0');
      return false;
    }

    // 2. localStorage
    var stored = safeGetLocalStorageItem(flagKey);
    if (stored === '1') return true;
    if (stored === '0') return false;

    // 3. Server value — auto-discovered flags store resolved value directly
    var entry = registry[flagKey];
    if (entry) {
      if (entry.serverValue !== undefined) {
        return entry.serverValue;
      }
      // Backward compat: manually registered flags with serverKey
      if (entry.serverKey && global[entry.serverKey] === true) {
        return true;
      }
    }

    return false;
  }

  /**
   * Return all registered flags and their resolved values.
   * @returns {Object<string, boolean>}
   */
  function getAll() {
    var result = {};
    for (var key in registry) {
      if (Object.prototype.hasOwnProperty.call(registry, key)) {
        result[key] = isEnabled(key);
      }
    }
    return result;
  }

  // --- Auto-discover server-backed flags from __SC_FLAGS ------------------

  _autoDiscover();

  // --- Pre-register client-only flags ------------------------------------

  register('sc_debug_gift');
  register('sc_gift_help_enabled');

  // --- Expose -------------------------------------------------------------

  var SnowcubesFlags = {
    register: register,
    isEnabled: isEnabled,
    getAll: getAll,
    _init: _init,
  };

  // CommonJS for tests, window global for browser
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = SnowcubesFlags;
  } else {
    global.SnowcubesFlags = SnowcubesFlags;
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : this);
